import { useState, useCallback, useRef } from "react";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * useRazorpay — dynamically loads the Razorpay checkout script
 * and exposes a promise-based `openCheckout` function.
 *
 * Usage:
 *   const { openCheckout, loading } = useRazorpay();
 *   await openCheckout(orderData, onSuccess, onFailure);
 */
const useRazorpay = () => {
  const [loading, setLoading] = useState(false);
  const scriptLoaded = useRef(false);

  /** Load the Razorpay checkout.js script once. */
  const loadScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (scriptLoaded.current || window.Razorpay) {
        scriptLoaded.current = true;
        resolve();
        return;
      }
      const tag = document.createElement("script");
      tag.src = RAZORPAY_SCRIPT_URL;
      tag.async = true;
      tag.onload = () => {
        scriptLoaded.current = true;
        resolve();
      };
      tag.onerror = () => reject(new Error("Failed to load Razorpay SDK."));
      document.body.appendChild(tag);
    });
  }, []);

  /**
   * Open the Razorpay checkout modal.
   *
   * @param {Object}   orderData              - Data returned by POST /api/payments/create-order
   * @param {string}   orderData.razorpayKeyId
   * @param {Object}   orderData.order        - { id, amount, currency }
   * @param {Object}   orderData.student      - { name, email, mobile }
   * @param {Function} onSuccess              - Called with Razorpay response { razorpay_order_id, razorpay_payment_id, razorpay_signature }
   * @param {Function} onFailure              - Called with error object
   */
  const openCheckout = useCallback(
    async (orderData, onSuccess, onFailure) => {
      setLoading(true);
      try {
        await loadScript();

        const options = {
          key: orderData.razorpayKeyId,
          amount: orderData.order.amount,
          currency: orderData.order.currency,
          name: "LMS Campus",
          description: orderData.description || "Fee Payment",
          order_id: orderData.order.id,
          prefill: {
            name: orderData.student?.name || "",
            email: orderData.student?.email || "",
            contact: orderData.student?.mobile || "",
          },
          theme: { color: "#174ea6" },
          handler: (response) => {
            setLoading(false);
            if (onSuccess) onSuccess(response);
          },
          modal: {
            ondismiss: () => {
              setLoading(false);
              if (onFailure) onFailure({ reason: "Payment cancelled by user." });
            },
          },
        };

        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", (resp) => {
          setLoading(false);
          if (onFailure) {
            onFailure({
              reason: resp.error?.description || "Payment failed.",
              code: resp.error?.code,
              metadata: resp.error?.metadata,
            });
          }
        });

        rzp.open();
      } catch (err) {
        setLoading(false);
        if (onFailure) onFailure({ reason: err.message || "Could not open Razorpay." });
      }
    },
    [loadScript],
  );

  return { openCheckout, loading };
};

export default useRazorpay;
